import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Search, Archive, RefreshCcw, Mail, Phone, MapPin, ChevronDown, ChevronUp, Download } from 'lucide-react';

export default function AdminFoundersArchive() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [snapshotting, setSnapshotting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [noteDraft, setNoteDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/founders-archive', { params: q ? { q } : {} });
      setRows(data.archive || []);
    } catch { toast.error('Failed to load archive'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const snapshot = async () => {
    if (!confirm('Snapshot every current Founders Club application into the permanent archive? Safe to run multiple times — updates existing rows.')) return;
    setSnapshotting(true);
    try {
      const { data } = await api.post('/admin/founders-archive/snapshot');
      toast.success(`Archived ${data.count} application${data.count === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Snapshot failed'); }
    finally { setSnapshotting(false); }
  };

  const addNote = async (appId) => {
    const text = (noteDraft[appId] || '').trim();
    if (!text) return;
    try {
      await api.post(`/admin/founders-archive/${appId}/note`, { note: text });
      setNoteDraft({ ...noteDraft, [appId]: '' });
      toast.success('Note added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const exportCsv = () => {
    const header = ['founder_number', 'name', 'email', 'phone', 'address', 'clothing_size', 'tier_name', 'status', 'admin_status', 'payment_status', 'created_at'];
    const csv = [header.join(',')];
    rows.forEach((r) => {
      const a = r.application || {};
      const p = (r.payments || [])[0] || {};
      const row = [a.founder_number, a.name, a.email, a.phone, a.address, a.clothing_size, a.tier_name, a.status, a.admin_status, p.status, a.created_at];
      csv.push(row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `founders-archive-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-founders-archive">
      <div className="h-panel-thin p-6">
        <div className="h-eyebrow flex items-center gap-2"><Archive size={12} /> Legacy archive</div>
        <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Founders Club Archive</div>
        <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>
          A permanent, admin-only record of every Founders Club applicant. This is preserved from the previous Founders Club era. Not visible to the public.
        </p>
      </div>

      <Panel className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search name / email / phone / founder #"
              className="pl-8 pr-3 py-1.5 rounded-full text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)', minWidth: 300 }}
              data-testid="archive-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={exportCsv} className="h-btn-outline text-xs"><Download size={12} className="mr-1" /> Export CSV</button>
            <button onClick={snapshot} disabled={snapshotting} className="h-btn text-xs" data-testid="archive-snapshot">
              <RefreshCcw size={12} className={`mr-1 ${snapshotting ? 'animate-spin' : ''}`} /> {snapshotting ? 'Archiving…' : 'Snapshot now'}
            </button>
          </div>
        </div>
        <div className="text-[11px] opacity-70 mt-2" style={{ color: 'var(--heritage-plum)' }}>
          Click "Snapshot now" once to capture the current Founders Club applications. Re-run any time to refresh the archive with the latest data.
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading…</div>}
      {!loading && rows.length === 0 && (
        <Panel className="p-8 text-center">
          <div className="h-eyebrow">Archive is empty</div>
          <div className="h-serif text-xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Click "Snapshot now" above to capture the current Founders Club data.</div>
        </Panel>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const a = r.application || {};
          const p = (r.payments || [])[0] || null;
          const isOpen = !!expanded[a.id];
          return (
            <Panel key={a.id} className="p-4" data-testid={`archive-row-${a.email}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="h-display text-xl" style={{ color: 'var(--heritage-plum)' }}>{a.name || '(no name)'}</span>
                    {a.founder_number && <span className="h-eyebrow text-[10px] opacity-80">{a.founder_number}</span>}
                    {a.tier_name && <span className="rounded-full px-2 py-0.5 text-[10px] h-serif uppercase tracking-wider" style={{ background: 'rgba(75,24,56,0.08)', color: 'var(--heritage-plum)' }}>{a.tier_name}</span>}
                    {a.admin_status && <span className="rounded-full px-2 py-0.5 text-[10px] h-serif uppercase tracking-wider" style={{ background: a.admin_status === 'approved' ? 'rgba(91,110,44,0.15)' : 'rgba(154,44,44,0.15)', color: 'var(--heritage-plum)' }}>{a.admin_status}</span>}
                  </div>
                  <div className="mt-1 text-sm flex flex-wrap gap-x-4 gap-y-1" style={{ color: 'var(--heritage-plum)' }}>
                    <span className="flex items-center gap-1 opacity-90"><Mail size={12} /> {a.email}</span>
                    {a.phone && <span className="flex items-center gap-1 opacity-90"><Phone size={12} /> {a.phone}</span>}
                  </div>
                  {a.address && <div className="mt-1 text-xs opacity-75 flex items-start gap-1" style={{ color: 'var(--heritage-plum)' }}><MapPin size={12} className="mt-0.5" /> {a.address}</div>}
                </div>
                <button onClick={() => setExpanded((e) => ({ ...e, [a.id]: !isOpen }))} className="h-btn-outline text-xs">
                  {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              </div>

              {isOpen && (
                <div className="mt-4 pt-4 border-t space-y-3 text-sm" style={{ borderColor: 'rgba(75,24,56,0.15)', color: 'var(--heritage-plum)' }}>
                  {a.reason && <div><div className="h-eyebrow text-[10px]">Their story</div><div className="italic opacity-90">"{a.reason}"</div></div>}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><div className="h-eyebrow text-[10px]">Application</div>
                      <ul className="mt-1 space-y-1 text-xs">
                        <li>Size: {a.clothing_size}</li>
                        <li>Submitted: {a.created_at}</li>
                        <li>Status: {a.status}</li>
                      </ul>
                    </div>
                    <div><div className="h-eyebrow text-[10px]">Payment</div>
                      {p ? (
                        <ul className="mt-1 space-y-1 text-xs">
                          <li>Ref: <span className="h-serif">{p.reference}</span></li>
                          <li>Amount: ${p.amount} {p.currency}</li>
                          <li>Plan: {p.plan_type}</li>
                          <li>Status: {p.status}</li>
                        </ul>
                      ) : <div className="text-xs opacity-70 mt-1">No payment on record.</div>}
                    </div>
                  </div>
                  <div>
                    <div className="h-eyebrow text-[10px]">Admin notes</div>
                    {(r.admin_notes || []).length === 0 && <div className="text-xs opacity-70 mt-1">No notes yet.</div>}
                    <ul className="mt-1 space-y-1 text-xs">
                      {(r.admin_notes || []).map((n, i) => <li key={`${n.at}-${i}`}><span className="opacity-70">{n.at?.slice(0, 10)} · {n.by}:</span> {n.note}</li>)}
                    </ul>
                    <div className="flex gap-2 mt-2">
                      <input value={noteDraft[a.id] || ''} onChange={(e) => setNoteDraft({ ...noteDraft, [a.id]: e.target.value })}
                        placeholder="Add a note…" className="flex-1 rounded-md px-2 py-1.5 text-xs"
                        style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }} />
                      <button onClick={() => addNote(a.id)} className="h-btn text-xs">Add</button>
                    </div>
                  </div>
                  <div className="text-[10px] opacity-60">Snapshotted {r.snapshot_at?.slice(0, 19).replace('T', ' ')}</div>
                </div>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
